import { Computer, Menu as MenuIcon, Close } from '@mui/icons-material';
import { AppBar, Box, Toolbar, Typography, IconButton, Drawer, List, ListItem, ListItemButton, ListItemText, useTheme, useMediaQuery } from '@mui/material';
import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';

const Header = () => {
  const [drawerOpen, setDrawerOpen] = useState(false);
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));

  const toggleDrawer = (open) => (event) => {
    if (event.type === 'keydown' && (event.key === 'Tab' || event.key === 'Shift')) {
      return;
    }
    setDrawerOpen(open);
  };

  const navLinks = [
    { text: 'Home', path: '/' },
    { text: 'About', path: '/Client/about' },
    { text: 'Executives', path: '/Client/executives' },
    { text: 'Gallery', path: '/Client/gallery' },
    { text: 'Registration', path: '/Client/registration' },
    { text: 'Contact', path: '/Client/contact' },
    { text: 'Member Portal', path: '/Client/login' },
  ];

  // Animation variants
  const linkVariants = {
    hover: {
      color: '#00d9ff',
      scale: 1.05,
      transition: {
        duration: 0.2
      }
    },
    tap: {
      scale: 0.95
    }
  };

  const drawerVariants = {
    hidden: { x: '100%' },
    visible: {
      x: 0,
      transition: {
        type: 'spring',
        stiffness: 300,
        damping: 30
      }
    },
    exit: {
      x: '100%',
      transition: {
        type: 'spring',
        stiffness: 300,
        damping: 30
      }
    }
  };

  return (
    <AppBar 
      component={motion.div}
      initial={{ y: -100 }}
      animate={{ y: 0 }}
      transition={{ duration: 0.5 }}
      sx={{ 
        backgroundColor: '#0a192f', 
        boxShadow: '0px 4px 20px rgba(0, 217, 255, 0.3)',
        backdropFilter: 'blur(10px)',
        background: 'rgba(10, 25, 47, 0.8)',
        borderBottom: '1px solid rgba(0, 217, 255, 0.2)'
      }}
    >
      <Toolbar sx={{ justifyContent: 'space-between' }}>
        {/* Logo & Title */}
        <Box 
          component={motion.div}
          whileHover={{ scale: 1.05 }}
          sx={{ display: 'flex', alignItems: 'center', gap: 1 }}
        >
          <Computer 
            component={motion.svg}
            animate={{ rotate: 360 }}
            transition={{ 
              repeat: Infinity,
              repeatType: 'loop',
              duration: 10,
              ease: 'linear'
            }}
            sx={{ fontSize: '40px', color: 'white' }} 
          />
          <Typography 
            component={motion.div}
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.2 }}
            sx={{ 
              fontWeight: '700', 
              fontSize: { xs: '18px', sm: '22px' }, 
              color: 'white',
              textShadow: '0 0 10px rgba(255,69,0,0.5)'
            }}
          >
            ICTAZ Nipa Chapter
          </Typography>
        </Box>

        {/* Navigation Links */}
        {isMobile ? (
          <>
            <IconButton 
              component={motion.button}
              whileHover={{ scale: 1.1 }}
              whileTap={{ scale: 0.9 }}
              color="inherit" 
              onClick={toggleDrawer(true)}
              sx={{ color: 'white' }}
            >
              <MenuIcon sx={{ fontSize: '30px' }} />
            </IconButton>
            
            <AnimatePresence>
              {drawerOpen && (
                <Drawer 
                  anchor="right" 
                  open={drawerOpen} 
                  onClose={toggleDrawer(false)}
                  PaperProps={{
                    sx: {
                      backgroundColor: 'rgba(10, 25, 47, 0.95)',
                      backdropFilter: 'blur(10px)',
                      borderLeft: '1px solid rgba(0, 217, 255, 0.2)'
                    }
                  }}
                >
                  <motion.div
                    style={{ height: '100%' }}
                    initial="hidden"
                    animate="visible"
                    exit="exit"
                    variants={drawerVariants}
                  >
                    <Box sx={{ display: 'flex', justifyContent: 'flex-end', p: 2 }}>
                      <IconButton 
                        component={motion.button}
                        whileHover={{ rotate: 90, scale: 1.1 }}
                        onClick={toggleDrawer(false)}
                        sx={{ color: 'white' }}
                      >
                        <Close sx={{ fontSize: '30px' }} />
                      </IconButton>
                    </Box>
                    
                    <List sx={{ width: 250, height: '100%', pt: 0 }}>
                      {navLinks.map((link, index) => (
                        <ListItem 
                          key={link.text} 
                          disablePadding
                          component={motion.div}
                          initial={{ x: 50, opacity: 0 }}
                          animate={{ 
                            x: 0, 
                            opacity: 1,
                            transition: { delay: index * 0.1 }
                          }}
                        >
                          <ListItemButton 
                            component={Link} 
                            to={link.path} 
                            onClick={toggleDrawer(false)}
                            sx={{
                              py: 2,
                              "&:hover": { 
                                backgroundColor: "rgba(0, 217, 255, 0.2)",
                                color: '#00d9ff'
                              },
                            }}
                          >
                            <ListItemText 
                              primary={link.text} 
                              sx={{ 
                                textAlign: 'center',
                                "& .MuiTypography-root": {
                                  fontWeight: '600',
                                  fontSize: '1.1rem'
                                }
                              }} 
                            />
                          </ListItemButton>
                        </ListItem>
                      ))}
                    </List>
                  </motion.div>
                </Drawer>
              )}
            </AnimatePresence>
          </>
        ) : (
          <Box 
            component={motion.div}
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.4 }}
            sx={{ display: 'flex', gap: 2 }}
          >
            {navLinks.map((link) => (
              <motion.div
                key={link.text}
                variants={linkVariants}
                whileHover="hover"
                whileTap="tap"
              >
                <Link 
                  to={link.path}
                  style={{
                    color: 'white',
                    textDecoration: 'none',
                    fontSize: '1.1em',
                    fontWeight: '500',
                    position: 'relative',
                    padding: '8px 12px'
                  }}
                >
                  {link.text}
                  <motion.span
                    style={{
                      position: 'absolute',
                      bottom: 0,
                      left: 0,
                      width: '100%',
                      height: '2px',
                      backgroundColor: '#00d9ff',
                      transformOrigin: 'right',
                      transform: 'scaleX(0)'
                    }}
                    whileHover={{
                      transformOrigin: 'left',
                      transform: 'scaleX(1)'
                    }}
                    transition={{ duration: 0.3 }}
                  />
                </Link>
              </motion.div>
            ))}
          </Box>
        )}
      </Toolbar>
    </AppBar>
  );
};

export default Header;